/**
 * web/src/hooks/mutations/useDisconnectGmail.ts
 *
 * Phase 6.5 UI-14 — TanStack mutation for POST /api/auth/gmail/disconnect.
 * Multi-account (2026-06): disconnect targets ONE GmailConnection — the
 * caller passes its id and the server deletes that row (cascading any
 * per-event defaults pointing at it).
 *
 * On success: invalidate qk.me() so GmailConnectionCard + Sidebar banner
 *             repaint without the removed connection; toast.success.
 * On error:   toast.error with the canonical "couldn't disconnect" copy.
 *
 * Cache invalidation scope: ONLY qk.me(). Do NOT invalidate workflow / gates /
 * tracked-emails queries — disconnecting Gmail does not change any workflow
 * state (UI-SPEC § "Critical: ONLY invalidate qk.me()").
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../../lib/queryKeys';
import { toast } from 'sonner';
import { ApiClientError } from '../../lib/api';

/**
 * Mutation hook for disconnecting one of the current user's Gmail connections.
 *
 * @returns React Query mutation handle. Caller invokes `mutate(connectionId)`.
 * @sideEffects HTTP POST /api/auth/gmail/disconnect with credentials:'include'
 *              and JSON body `{ connectionId }`;
 *              Sends `X-Requested-With: fetch` header to satisfy backend CSRF
 *              guard (CR-01). On success: invalidates qk.me() + success toast;
 *              on error: error toast.
 * @idempotent no — a second call for the same id 404s (row already deleted);
 *             the UI disables the confirm button while pending.
 */
export function useDisconnectGmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (connectionId: string) => {
      const res = await fetch('/api/auth/gmail/disconnect', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'fetch',
        },
        body: JSON.stringify({ connectionId }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new ApiClientError(
          res.status,
          'DISCONNECT_FAILED',
          undefined,
          text || res.statusText || 'Disconnect failed',
        );
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.me() });
      toast.success('Gmail account disconnected.');
    },
    onError: () => {
      toast.error("Couldn't disconnect Gmail — try again.");
    },
  });
}
